/**
 * Signed-link lifetime the audio player requests, in seconds.
 * The API caps this at one hour (`MAX_TEMPORARY_LINK_TTL_SECONDS`).
 * The default 60s link expires while a voicemail is still playing.
 */
export const AUDIO_PLAYBACK_SIGNED_URL_TTL_SECONDS = 60 * 60;

/** Prefetch the next signed link this long before the current one expires. */
export const SIGNED_PLAYBACK_URL_REFRESH_LEAD_MS = 10_000;

const PLAYABLE_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/m4a',
  'audio/aac',
  'audio/mp4',
  'audio/ogg',
  'audio/webm',
  'audio/3gpp',
  'audio/3gpp2',
  'audio/amr',
  'audio/amr-nb',
  'audio/flac',
  'audio/x-ms-wma',
];

/**
 * Whether the in-browser player can play this MIME type.
 * A missing type stays enabled so callers that omit it still render controls.
 *
 * @param mimeType File MIME type, when known.
 * @returns True when playback controls should be enabled.
 */
export function isPlayableAudioMimeType(mimeType?: string): boolean {
  if (!mimeType) {
    return true;
  }
  return PLAYABLE_AUDIO_MIME_TYPES.includes(mimeType);
}

export interface PlaybackSourceDecisionInput {
  /** URL currently assigned to the media element. */
  activeUrl: string | null;
  /** Absolute expiry of `activeUrl`, or null when unknown. */
  activeExpiresAtMs: number | null;
  /** Seconds already played on `activeUrl`. */
  playedSeconds: number;
  /** True while the user has started playback and has not paused. */
  playing: boolean;
  nextUrl: string;
  nextExpiresAtMs: number;
  nowMs: number;
  /** True after the media element reports the active source failed. */
  mediaFailed: boolean;
}

export interface PlaybackSourceDecision {
  /** URL the media element should use. */
  url: string;
  /**
   * Seconds to seek after the element loads `url`.
   * Null when the source is unchanged or playback has not started.
   */
  resumeSeconds: number | null;
}

/**
 * Delay before prefetching the next signed playback URL.
 *
 * @param expiresAtMs Absolute expiry timestamp in milliseconds.
 * @param nowMs Current time in milliseconds.
 * @returns Milliseconds until the prefetch should run. `0` when already due.
 */
export function signedPlaybackUrlRefreshDelayMs(
  expiresAtMs: number,
  nowMs: number
): number {
  if (!Number.isFinite(expiresAtMs) || !Number.isFinite(nowMs)) {
    return 0;
  }
  return Math.max(
    0,
    expiresAtMs - nowMs - SIGNED_PLAYBACK_URL_REFRESH_LEAD_MS
  );
}

/**
 * Choose the media-element URL after a signed link arrives.
 *
 * Replacing the element source reloads it from the beginning. A still-valid
 * source stays in place while playback is underway. An expired or failed
 * source is replaced and playback resumes at the previous position.
 *
 * @param input Active source, the newly signed link, and the current time.
 * @returns The URL to assign and an optional resume position in seconds.
 */
export function resolvePlaybackSource(
  input: PlaybackSourceDecisionInput
): PlaybackSourceDecision {
  const hasProgress = input.playing || input.playedSeconds > 0;
  const activeStillValid =
    input.activeUrl != null &&
    input.activeExpiresAtMs != null &&
    input.activeExpiresAtMs > input.nowMs;

  if (
    input.activeUrl &&
    hasProgress &&
    activeStillValid &&
    !input.mediaFailed
  ) {
    return { url: input.activeUrl, resumeSeconds: null };
  }

  const sourceChanged =
    input.activeUrl != null && input.activeUrl !== input.nextUrl;
  const resumeSeconds =
    hasProgress && sourceChanged ? input.playedSeconds : null;

  return { url: input.nextUrl, resumeSeconds };
}
